import { UISref, useRouter } from "@uirouter/react";
import { useAtom } from "jotai";
import moment from "moment";
import { FunctionComponent, useCallback, useMemo } from "react";
import styled from "styled-components";
import { PrimaryButton } from "../../../components/button";
import { Icon } from "../../../components/icons";
import { KycIcon } from "../../../components/icons/status";
import { ellipsis } from "../../../components/layout";
import { Table } from "../../../components/table";
import { TableHeaders } from "../../../components/table/Head";
import { UsersDTO } from "../../../store/users/models/dto";
import { colours } from "../../../styles/colours";
import { names as routes } from "../../merchants/route-names";
import { merchantPagination } from "./atoms";
import { NoMerchants } from "./base";

type SingleMerchant = UsersDTO.Responses.GetSingle.T;

type EnhancedSingleMerchant = UsersDTO.Responses.GetSingle.T & {
  detailsEnabled: boolean;
  detailsEmailVerified: boolean;
  detailsAccountLocked: boolean;
  detailsCredentialsExpired: boolean;
  detailsLoginTries: number;
};

type fieldNames = keyof EnhancedSingleMerchant;

const headers: TableHeaders<fieldNames> = [
  { name: "companyDetails", title: "Company Name" },
  {
    name: "createdDate",
    title: "Date Created",
  },
  {
    name: "detailsEnabled",
    title: "Enabled",
    hiddenMobile: true,
    centered: true,
  },
  {
    name: "detailsEmailVerified",
    title: "Email Verified",
    hiddenMobile: true,
    centered: true,
  },
  {
    name: "detailsAccountLocked",
    title: "Account Locked",
    hiddenMobile: true,
    centered: true,
  },
  {
    name: "detailsCredentialsExpired",
    title: "Credentials Expired",
    hiddenMobile: true,
    centered: true,
  },
  {
    name: "userReferenceId",
    title: "",
    centered: true,
    hiddenMobile: true,
  },
];

const OverflowField = styled.span`
  ${ellipsis};
  max-width: 15em;
  display: inline-block;
`;

type MerchantListProps = {
  usersList: UsersDTO.Responses.GetAllMerchants.T;
};

export const PresentationalMerchantsList: FunctionComponent<MerchantListProps> = ({
  usersList,
}) => {
  const router = useRouter();
  const [pageableState, onPaginationChange] = useAtom(merchantPagination);

  const newUserListContent: Array<EnhancedSingleMerchant> = useMemo(
    () =>
      usersList.content.map((x) => ({
        ...x,
        detailsEnabled: x.userAccountDetails.enabled,
        detailsEmailVerified: x.userAccountDetails.emailVerified,
        detailsAccountLocked: x.userAccountDetails.accountNonLocked,
        detailsCredentialsExpired: x.userAccountDetails.credentialsNonExpired,
        detailsLoginTries: x.userAccountDetails.loginTries,
      })),
    [usersList.content]
  );

  const renderedFields: Partial<
    Record<fieldNames, FunctionComponent<EnhancedSingleMerchant>>
  > = useMemo(
    () => ({
      userReferenceId: ({ userReferenceId }) => (
        <UISref to={routes.single} params={{ id: userReferenceId }}>
          <a>
            <PrimaryButton variant="contained">
              <Icon type="export" rotateAmount={270} colour1={colours.white} />
            </PrimaryButton>
          </a>
        </UISref>
      ),
      companyDetails: ({ companyDetails }) => (
        <OverflowField>{companyDetails?.name}</OverflowField>
      ),
      createdDate: ({ createdDate }) => (
        <>{moment.utc(createdDate).local().format("yyy-MM-DD HH:mm")}</>
      ),
      kycStatus: ({ kycStatus }) => KycIcon(kycStatus, "kyc"),
      // detailsAccountLocked: ({ detailsAccountLocked, userReferenceId }) =>
      //   detailsAccountLocked ? (
      //     <BooleanField colour1={colours.green} />
      //   ) : (
      //     <LockingTableCTA userId={userReferenceId} type="unlock" />
      //   ),
    }),
    [routes.single, colours.white, colours.green]
  );

  const rowOnClick: (s: EnhancedSingleMerchant) => void = useCallback(
    ({ userReferenceId }) => {
      router.stateService.go(routes.single, {
        id: userReferenceId,
      });
    },
    []
  );

  return (
    <>
      {usersList.pageable.totalElements < 1 ? (
        <NoMerchants />
      ) : (
        <>
          <Table
            data={newUserListContent}
            head={headers}
            renderFields={renderedFields}
            unsortable={[
              "detailsEnabled",
              "detailsEmailVerified",
              "detailsAccountLocked",
              "detailsCredentialsExpired",
              "detailsLoginTries",
            ]}
            rowOnClick={rowOnClick}
            pagination={usersList.pageable}
            onPaginationChange={onPaginationChange}
            pageableState={pageableState}
          />
        </>
      )}
    </>
  );
};
